/**
 * <cart-announcement> — the message strip at the top of the cart drawer.
 *
 * Owns three behaviours, each opt-in via attribute:
 *   - Countdown. `timer-minutes` starts a deadline; `{{ timer }}` in the message becomes a
 *     live mm:ss readout.
 *   - Rotation. `rotate-interval` cycles between the two or three configured messages.
 *   - Dismissal. `dismissible` shows a close button and remembers the choice.
 *
 * State lives in `sessionStorage`, never in the markup. The drawer is server-re-rendered on
 * every cart mutation, so a deadline written into the HTML would restart each time the shopper
 * changed a quantity — which is exactly the moment the urgency is supposed to hold. Keying off
 * storage means a fresh element picks up the running deadline instead.
 *
 * Attributes:
 *   timer-minutes    Countdown length in minutes. Absent disables the countdown.
 *   timer-key        sessionStorage key for the deadline. Per block, so two announcements can
 *                    run independent timers.
 *   timer-scope      'session' keeps one deadline for the whole browsing session;
 *                    'visit' restarts it on every page load.
 *   on-expiry        'message' swaps in `expired-message`, 'hide' removes the strip,
 *                    'restart' loops the countdown.
 *   expired-message  Plain text shown when `on-expiry="message"`.
 *   rotate-interval  Milliseconds between messages. Absent or with one message, no rotation.
 *   dismissible      Renders as dismissible; pairs with `dismiss-key`.
 *   dismiss-key      sessionStorage key recording the dismissal.
 *
 * Children (queried, not passed):
 *   [data-cart-announcement-message]  One per configured message.
 *   [data-cart-announcement-timer]    Placeholder the mm:ss readout is written into.
 *   [data-cart-announcement-dismiss]  The close button.
 */
class CartAnnouncement extends HTMLElement {
  /** @type {number | null} */
  #tickHandle = null;

  /** @type {number | null} */
  #rotateHandle = null;

  /** @type {AbortController | null} */
  #controller = null;

  #messageIndex = 0;

  #expired = false;

  connectedCallback() {
    if (this.#isDismissed()) {
      this.hidden = true;
      return;
    }

    this.#controller = new AbortController();

    this.querySelector('[data-cart-announcement-dismiss]')?.addEventListener('click', this.#dismiss, {
      signal: this.#controller.signal,
    });

    this.#startCountdown();
    this.#startRotation();
  }

  disconnectedCallback() {
    this.#stopTimers();
    this.#controller?.abort();
    this.#controller = null;
  }

  #stopTimers() {
    if (this.#tickHandle !== null) clearInterval(this.#tickHandle);
    if (this.#rotateHandle !== null) clearInterval(this.#rotateHandle);
    this.#tickHandle = null;
    this.#rotateHandle = null;
  }

  /* ── Storage ──────────────────────────────────────────────────────────────── */

  /**
   * sessionStorage throws in Safari private mode and when cookies are blocked, and a
   * countdown is never worth breaking the cart over — degrade to a per-render timer instead.
   *
   * @param {string} key
   * @returns {string | null}
   */
  #read(key) {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  /**
   * @param {string} key
   * @param {string} value
   */
  #write(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      /* storage unavailable — the timer still runs for this render */
    }
  }

  #isDismissed() {
    const key = this.getAttribute('dismiss-key');
    return Boolean(key) && this.#read(/** @type {string} */ (key)) === '1';
  }

  #dismiss = () => {
    const key = this.getAttribute('dismiss-key');
    if (key) this.#write(key, '1');
    this.hidden = true;
    this.#stopTimers();
  };

  /* ── Countdown ────────────────────────────────────────────────────────────── */

  /** @returns {HTMLElement[]} */
  get #timerSlots() {
    return Array.from(this.querySelectorAll('[data-cart-announcement-timer]'));
  }

  #startCountdown() {
    const minutes = Number(this.getAttribute('timer-minutes'));
    if (!minutes || !Number.isFinite(minutes) || this.#timerSlots.length === 0) return;

    const deadline = this.#resolveDeadline(minutes);
    if (deadline === null) return;

    this.#renderCountdown(deadline);
    this.#tickHandle = window.setInterval(() => this.#renderCountdown(deadline), 1000);
  }

  /**
   * Resolves the moment the countdown ends, reusing a stored deadline when the merchant asked
   * for a session-long timer and it has not already run out.
   *
   * @param {number} minutes
   * @returns {number | null} Epoch milliseconds.
   */
  #resolveDeadline(minutes) {
    const durationMs = minutes * 60 * 1000;
    const key = this.getAttribute('timer-key');
    const perSession = this.getAttribute('timer-scope') !== 'visit' && Boolean(key);

    if (perSession) {
      const stored = Number(this.#read(/** @type {string} */ (key)));
      if (stored && stored > Date.now()) return stored;
    }

    const deadline = Date.now() + durationMs;
    if (perSession) this.#write(/** @type {string} */ (key), String(deadline));
    return deadline;
  }

  /** @param {number} deadline */
  #renderCountdown(deadline) {
    const remaining = deadline - Date.now();

    if (remaining <= 0) {
      this.#handleExpiry();
      return;
    }

    const totalSeconds = Math.floor(remaining / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const text = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

    for (const slot of this.#timerSlots) {
      slot.textContent = text;
    }
  }

  #handleExpiry() {
    if (this.#expired) return;
    this.#expired = true;
    this.#stopTimers();

    switch (this.getAttribute('on-expiry')) {
      case 'hide':
        this.hidden = true;
        break;

      case 'restart': {
        // Clear the stored deadline first, or `#resolveDeadline` would hand back the one that
        // just elapsed and the countdown would sit at 00:00 forever.
        const key = this.getAttribute('timer-key');
        if (key) this.#write(key, '');
        this.#expired = false;
        this.#startCountdown();
        break;
      }

      default: {
        const message = this.getAttribute('expired-message');
        if (!message) return;

        const container = this.querySelector('.cart-announcement__messages');
        if (!container) return;

        // textContent, not innerHTML: this string comes straight from a theme setting.
        container.textContent = message;
      }
    }
  }

  /* ── Rotation ─────────────────────────────────────────────────────────────── */

  #startRotation() {
    const interval = Number(this.getAttribute('rotate-interval'));
    const messages = this.querySelectorAll('[data-cart-announcement-message]');
    if (!interval || messages.length < 2) return;

    this.#rotateHandle = window.setInterval(() => {
      messages[this.#messageIndex]?.setAttribute('hidden', '');
      this.#messageIndex = (this.#messageIndex + 1) % messages.length;
      messages[this.#messageIndex]?.removeAttribute('hidden');
    }, interval);
  }
}

if (!customElements.get('cart-announcement')) {
  customElements.define('cart-announcement', CartAnnouncement);
}
